export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'expired' | 'refunded'

export type PaymentMethod = 'virtual_account' | 'qris' | 'ewallet' | 'card'

export type PaymentChannel =
  | 'va_bca'
  | 'va_bri'
  | 'va_mandiri'
  | 'va_bni'
  | 'qris'
  | 'ewallet_gopay'
  | 'ewallet_ovo'
  | 'ewallet_dana'
  | 'ewallet_shopeepay'
  | 'card'

export interface PaymentTransaction {
  id: string
  enrollmentId: string
  externalId: string
  paymentChannel: PaymentChannel
  paymentMethod: PaymentMethod
  amount: number
  fee: number
  discount: number
  netAmount: number
  status: PaymentStatus
  paymentCode?: string
  paymentUrl?: string
  expiryTime?: string
  paidAt?: string
  couponCode?: string
  createdAt: string
  updatedAt: string
}

export interface CreatePaymentRequest {
  enrollmentId: string
  paymentChannel: PaymentChannel
  customerName: string
  customerEmail: string
  customerPhone?: string
  couponCode?: string
  discountAmount?: number
}

export interface RetryPaymentRequest {
  paymentChannel: PaymentChannel
  customerName: string
  customerEmail: string
  customerPhone?: string
}

export interface PaymentChannelInfo {
  channel: PaymentChannel
  method: PaymentMethod
  name: string
  icon: string
  description: string
}

export const PAYMENT_CHANNELS: PaymentChannelInfo[] = [
  {
    channel: 'va_bca',
    method: 'virtual_account',
    name: 'BCA Virtual Account',
    icon: '🏦',
    description: 'Transfer via BCA Virtual Account',
  },
  {
    channel: 'va_bri',
    method: 'virtual_account',
    name: 'BRI Virtual Account',
    icon: '🏦',
    description: 'Transfer via BRI Virtual Account',
  },
  {
    channel: 'va_mandiri',
    method: 'virtual_account',
    name: 'Mandiri Virtual Account',
    icon: '🏦',
    description: 'Transfer via Mandiri Virtual Account',
  },
  {
    channel: 'va_bni',
    method: 'virtual_account',
    name: 'BNI Virtual Account',
    icon: '🏦',
    description: 'Transfer via BNI Virtual Account',
  },
  {
    channel: 'qris',
    method: 'qris',
    name: 'QRIS',
    icon: '📱',
    description: 'Scan QR code with any e-wallet',
  },
  {
    channel: 'ewallet_gopay',
    method: 'ewallet',
    name: 'GoPay',
    icon: '💚',
    description: 'Pay with GoPay',
  },
  {
    channel: 'ewallet_ovo',
    method: 'ewallet',
    name: 'OVO',
    icon: '💜',
    description: 'Pay with OVO',
  },
  {
    channel: 'ewallet_dana',
    method: 'ewallet',
    name: 'DANA',
    icon: '💙',
    description: 'Pay with DANA',
  },
  {
    channel: 'ewallet_shopeepay',
    method: 'ewallet',
    name: 'ShopeePay',
    icon: '🧡',
    description: 'Pay with ShopeePay',
  },
  {
    channel: 'card',
    method: 'card',
    name: 'Credit/Debit Card',
    icon: '💳',
    description: 'Pay with Visa, Mastercard, or JCB',
  },
]

export function getPaymentChannelInfo(channel: PaymentChannel): PaymentChannelInfo | undefined {
  return PAYMENT_CHANNELS.find((c) => c.channel === channel)
}

export function groupPaymentChannelsByMethod(): Record<PaymentMethod, PaymentChannelInfo[]> {
  return PAYMENT_CHANNELS.reduce(
    (acc, channel) => {
      if (!acc[channel.method]) {
        acc[channel.method] = []
      }
      acc[channel.method].push(channel)
      return acc
    },
    {} as Record<PaymentMethod, PaymentChannelInfo[]>
  )
}

export function formatPaymentMethodLabel(method: PaymentMethod): string {
  switch (method) {
    case 'virtual_account':
      return 'Virtual Account'
    case 'qris':
      return 'QRIS'
    case 'ewallet':
      return 'E-Wallet'
    case 'card':
      return 'Credit/Debit Card'
    default:
      return method
  }
}
